import type { ComponentProps } from 'react'
import { ContextMenu as BaseContextMenu } from '@base-ui/react/context-menu'
import { Menu as BaseMenu } from '@base-ui/react/menu'

// Base UI primitives (re-exported as-is). `CheckboxItemIndicator` and
// `RadioItemIndicator` are intentionally not re-exported — the styled
// `CheckboxItem` and `RadioItem` below render the MUI Checkbox / Radio
// visual themselves, so consumers don't need to compose an indicator.
const { Root, Portal, Arrow, Backdrop, SubmenuRoot, Viewport } = BaseMenu

export { Arrow, Backdrop, Portal, Root, SubmenuRoot, Viewport }
export { Trigger } from './trigger'

// A right-click (or long-press) menu: `ContextRoot` + `ContextTrigger` (the area that
// opens it at the pointer) take the place of `Root` + `Trigger`. Every other part below
// is the same, because Base UI builds its context menu from the menu parts.
const { Root: ContextRoot, Trigger: ContextTrigger } = BaseContextMenu

export { ContextRoot, ContextTrigger }

/**
 * Second argument of `Menu.Root`'s `onOpenChange` — carries the `reason` the
 * menu opened/closed (trigger press, hover, outside press, escape, …). Owned
 * here so consumers don't reconstruct it from base-ui prop internals.
 */
export type MenuOpenChangeDetails = Parameters<
  NonNullable<ComponentProps<typeof Root>['onOpenChange']>
>[1]

/** True when the open-change was driven by pressing the trigger (vs hover, outside press, etc.). */
export const isTriggerPress = (details: MenuOpenChangeDetails) => details.reason === 'trigger-press'

// Custom styled components
export { CheckboxItem } from './checkbox-item'
export { Group } from './group'
export { GroupLabel } from './group-label'
export { Item } from './item'
export { LinkItem } from './link-item'
export { Popup } from './popup'
export { Positioner } from './positioner'
export { RadioGroup } from './radio-group'
export { RadioItem } from './radio-item'
export { Separator } from './separator'
export { SubmenuTrigger } from './submenu-trigger'
